import posthog from 'posthog-js';

/**
 * PostHog analytics + session replay + exception capture.
 *
 * Stands in for Sentry, which is installed but was never configured (no DSN exists
 * in .env or .env.example, and Sentry.init is never called anywhere) — so
 * captureException/withProfiler in AuthAwareErrorBoundary and App were no-ops.
 *
 * The project API key is a public, write-only ingestion token and is designed to
 * ship in client bundles. It is inlined as a fallback so the integration works
 * without a Netlify env var; set VITE_POSTHOG_KEY to override.
 */
const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY ||
  'phc_yJiUKwVA4KupXcsN52QsfTeGeUMaJEJmDLab8Pipk8Xx';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let ready = false;

/**
 * Analytics must never be able to break an order. Every public function routes
 * through this, so a PostHog outage or an adblocker degrades to a no-op rather
 * than throwing inside the checkout path.
 */
function safely(fn: () => void) {
  if (!ready) return;
  try {
    fn();
  } catch (err) {
    console.warn('[analytics] non-fatal', err);
  }
}

export function initAnalytics() {
  if (ready || !POSTHOG_KEY) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Guests are real (anonymous) auth users, and we want the pre-login
      // checkout funnel too, so profiles are created for everyone.
      person_profiles: 'always',
      // SPA: React Router changes history without a page load.
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Replaces the dead Sentry pipeline.
      capture_exceptions: true,
      // "Customer tapped and nothing happened" is the signal we most want tonight.
      capture_dead_clicks: true,
      // Surfaces console errors inline in the replay timeline.
      enable_recording_console_log: true,
      mask_personal_data_properties: true,
      session_recording: {
        // Card fields live in Stripe's cross-origin iframe and are never
        // recordable regardless. This masks the fields that ARE capturable —
        // name, email, phone, address. Sessions stay attributable via
        // identifyUser() below, which is more precise than reading keystrokes.
        maskAllInputs: true,
      },
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        // Don't pollute the project with local dev traffic.
        if (import.meta.env.DEV) ph.opt_out_capturing();
      },
    });
    ready = true;
  } catch (err) {
    console.warn('[analytics] init failed', err);
  }
}

/** Attach a session to a known person. Guests included — they have real anon auth IDs. */
export function identifyUser(
  userId: string,
  props?: { email?: string; isGuest?: boolean; phone?: string; name?: string }
) {
  safely(() => {
    posthog.identify(userId, {
      email: props?.email,
      is_guest: props?.isGuest ?? false,
      // Phone is how support reaches a guest who had a problem.
      phone: props?.phone,
      name: props?.name,
    });
  });
}

export function resetUser() {
  safely(() => posthog.reset());
}

export function track(event: string, props?: Record<string, unknown>) {
  safely(() => posthog.capture(event, props));
}

/**
 * Tag the current session with order identifiers so a session can be found by
 * order id or pickup code — the lookup support actually needs when a customer
 * says "my order didn't come through".
 */
export function tagOrder(orderId: string, pickupCode?: string | null, venueId?: string | null) {
  safely(() => {
    posthog.register({
      order_id: orderId,
      pickup_code: pickupCode ?? undefined,
      venue_id: venueId ?? undefined,
    });
  });
}

/** Manual exception capture for caught errors that would otherwise be swallowed. */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  safely(() => {
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, context);
  });
}

/** Direct link to the replay for the live session — printed for fast triage. */
export function currentSessionReplayUrl(): string | null {
  if (!ready) return null;
  try {
    return posthog.get_session_replay_url({ withTimestamp: true }) ?? null;
  } catch {
    return null;
  }
}
